// Import FeedbackModal
import { useAuth } from "@/components/AuthContext";
import SkeletonLoader from "@/components/SkeletonLoader";
import { SpatiusPrimarySessionRoot } from "@/components/spatius-avatar";
import { ClassroomModal } from "@/components/student/ClassroomModal";
import { useClassEnrollment } from "@/hooks/useClassEnrollment";
import type { Conversation } from "@/hooks/useConversationHistory";
import {
  hasVerifiedRevenueCatAccess,
  useRevenueCatSubscription,
} from "@/hooks/useRevenueCatSubscription";
import { useRouter } from "@/hooks/useSchoolRouter";
import { type AppBrandConfig, DEFAULT_APP_BRAND } from "@/lib/brands";
import {
  type ContinueConversationResult,
  ConversationAPIError,
  continueConversation,
  deleteConversationAPI,
} from "@/lib/conversationAPI";
import {
  CONVERSATION_START_CHOICE_STORAGE_KEY,
  type ConversationStartChoice,
  isWebConversationContinuationEnabled,
} from "@/lib/conversationContinuation";
import { getPlatform } from "@/lib/pwa-utils";
import {
  AnimatePresence,
  MotionConfig,
  motion,
  useMotionValueEvent,
  useScroll,
} from "framer-motion";
import { Room, RoomEvent } from "livekit-client";
import dynamic from "next/dynamic";
import { usePostHog } from "posthog-js/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import AboutModal from "../components/AboutModal";
import FeedbackModal from "../components/FeedbackModal";
import FreemiumPreSessionOfferModal from "../components/FreemiumPreSessionOfferModal";
import { GooglePlayBadgeLink } from "../components/GooglePlayBadgeLink";
import { GooglePlayMobileAction } from "../components/GooglePlayMobileAction";
import HamburgerMenu from "../components/HamburgerMenu";
import { HowItWorksModal } from "../components/HowItWorksModal";
import PastConversationsUpgradeModal from "../components/PastConversationsUpgradeModal";
import StreakDisplay from "../components/StreakDisplay";
// Import modular Fullscreen components
import {
  AccountAccess,
  AsciiLogoOverlay,
  ChickyLogo,
  DecorativeBackground,
  Tagline,
  UpgradeButton,
} from "../components/fullscreen";
import languages from "../data/languages.json";
import {
  exposeGuestSpatiusAvatarExperiment,
  useGuestSpatiusAvatarExperiment,
} from "../hooks/useGuestSpatiusAvatarExperiment";
import {
  type InitialCustomInstructionsLifetime,
  useInitialCustomInstructions,
} from "../hooks/useInitialCustomInstructions";
import useKeyboardShortcuts from "../hooks/useKeyboardShortcuts";
import useLiveKitAgent from "../hooks/useLiveKitAgent";
import { useUserPreferences } from "../hooks/useLocalStorage";
import { useSpatiusSdkPreinit } from "../hooks/useSpatiusSdkPreinit";
import { useStreak } from "../hooks/useStreak";
import { useUserLevel } from "../hooks/useUserLevel";
import {
  exposeWebappSignupBeforeDemoExperiment,
  useWebappSignupBeforeDemoExperiment,
} from "../hooks/useWebappSignupBeforeDemoExperiment";
import type { AppShellLayout } from "../lib/appShellLayout";
import { getOrCreateClientId } from "../lib/clientId";
import { GUEST_SPATIUS_AVATAR_VARIANTS } from "../lib/experiments/guestSpatiusAvatar";
import {
  WEBAPP_SIGNUP_BEFORE_DEMO_QUERY_KEY,
  WEBAPP_SIGNUP_BEFORE_DEMO_READY_QUERY_KEY,
  WEBAPP_SIGNUP_BEFORE_DEMO_VARIANTS,
  buildWebappSignupBeforeDemoAuthUrl,
  getWebappSignupBeforeDemoAttributionProperties,
  restoreWebappSignupBeforeDemoExposureFromSearch,
} from "../lib/experiments/webappSignupBeforeDemo";
import { resolveLanguageCodeFromParam } from "../lib/language-pair-resolver";
import {
  getDefaultLanguageJourneyChapter,
  markLanguageJourneyChapterComplete,
  syncLanguageJourneyProgress,
} from "../lib/languageJourney";
import {
  deriveStandardLearningMode,
  getGuidedInstructionLanguageCode,
  hasSameBaseLanguage,
} from "../lib/learning-mode";
import { capCustomInstructionsInput } from "../lib/promptPersonalization";
import { shouldPrewarm } from "../lib/session-prewarm-policy";
import {
  type PreSessionVariant,
  type SessionAnalyticsSurface,
  type SessionLaunchSurface,
  type SessionPreferenceStorage,
  type SessionPrewarmPolicy,
} from "../lib/sessionLaunchPresets";
import { type SessionMode, parseSessionMode } from "../lib/sessionRouting";
import {
  SESSION_VOCABULARY_SELECTION_EVENT,
  type SessionVocabularyContext,
  loadSessionVocabularySelection,
} from "../lib/sessionVocabulary";
import { supabase } from "../lib/supabaseClient";
import { resolveTranslationLanguageCode } from "../lib/translation-language";
import { assignSpeakersToSegments, formatRecordingTime } from "../lib/utils";
import { TranscriptSegment } from "../models/podcast";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import ListenView from "./ListenView";
import { usePlayer } from "./player/PlayerContext";

// Import useAuth

interface Languages {
  [key: string]: string;
}
const languagesData: Languages = languages;
const PREWARM_ENABLED = process.env.NEXT_PUBLIC_PREWARM_ENABLED?.toLowerCase() === "true";
const FULLSCREEN_GUEST_SESSION_SECONDS = 300;
const JOURNEY_AUTO_COMPLETE_SECONDS = 240;
const ENGLISH_TARGET_LANGUAGE_CODES = new Set(["en-US", "en-GB", "en-AU", "en-IN"]);
const SPANISH_INSTRUCTION_LANGUAGE_CODE = "es-ES";
const ENGLISH_INSTRUCTION_LANGUAGE_CODE = "en-US";
const LANGUAGE_LEVEL_VALUES = [
  "absolute_beginner",
  "beginner",
  "intermediate",
  "advanced",
] as const;
type LanguageLevel = (typeof LANGUAGE_LEVEL_VALUES)[number];

function DeferredViewSkeleton({ label = "Loading view..." }: { label?: string }) {
  return (
    <div className="flex min-h-[60vh] w-full items-center justify-center bg-[#0f0f14] px-4 py-12 text-white">
      <div className="w-full max-w-4xl space-y-5">
        <div className="h-5 w-40 animate-pulse rounded-full bg-white/10" aria-hidden="true" />
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 shadow-2xl shadow-black/20">
          <div className="mb-5 h-8 w-2/3 animate-pulse rounded-lg bg-white/10" />
          <div className="grid gap-3 md:grid-cols-3">
            <div className="h-28 animate-pulse rounded-xl bg-white/[0.06]" />
            <div className="h-28 animate-pulse rounded-xl bg-white/[0.06]" />
            <div className="h-28 animate-pulse rounded-xl bg-white/[0.06]" />
          </div>
          <div className="mt-5 space-y-3">
            <div className="h-4 w-full animate-pulse rounded bg-white/[0.06]" />
            <div className="h-4 w-5/6 animate-pulse rounded bg-white/[0.06]" />
            <div className="h-4 w-3/5 animate-pulse rounded bg-white/[0.06]" />
          </div>
        </div>
        <p className="text-sm font-medium text-white/55">{label}</p>
      </div>
    </div>
  );
}

const AccountView = dynamic(() => import("./AccountView"), {
  loading: () => <DeferredViewSkeleton label="Loading account..." />,
  ssr: false,
});

const PracticeView = dynamic(() => import("./PracticeView"), {
  loading: () => <DeferredViewSkeleton label="Loading practice chat..." />,
  ssr: false,
});

const ProgressView = dynamic(() => import("./ProgressView"), {
  loading: () => <DeferredViewSkeleton label="Loading progress..." />,
  ssr: false,
});

function resolveLanguageLevelFromParam(value?: string | null): LanguageLevel | null {
  if (!value) return null;
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return LANGUAGE_LEVEL_VALUES.includes(normalized as LanguageLevel)
    ? (normalized as LanguageLevel)
    : null;
}

function getDefaultInstructionLanguageCodeForTarget(languageCode: string) {
  return ENGLISH_TARGET_LANGUAGE_CODES.has(languageCode)
    ? SPANISH_INSTRUCTION_LANGUAGE_CODE
    : ENGLISH_INSTRUCTION_LANGUAGE_CODE;
}

// ChickyLogo, DecorativeBackground, and other UI components now imported from components/fullscreen

interface MainAppViewProps {
  children?: React.ReactNode;
  brand?: AppBrandConfig;
  segments: TranscriptSegment[];
  currentTime: number;
  currentSegment: TranscriptSegment | undefined;
  showTranslation: boolean;
  interactionMode: "passive" | "text" | "voice";
  isPlaying: boolean;
  togglePlay: () => void;
  onSegmentClick: (time: number) => void;
  onExplainSegment: (segment: TranscriptSegment) => void;
  setShowTranslation: (show: boolean) => void;
  setInteractionMode: (mode: "passive" | "text" | "voice") => void;
  getSegmentClasses: (segment: TranscriptSegment, index: number) => string; // Add index parameter
  awaitingUserResponse: boolean;
  initialTargetLanguageParam?: string | null;
  initialInstructionLanguageParam?: string | null;
  initialLanguageLevelParam?: string | null;
  initialSessionModeParam?: string | null;
  initialJourneyChapterIdParam?: string | null;
  initialPodcastDiscussionIdParam?: string | null;
  initialCustomInstructionsParam?: string | null;
  initialCustomInstructionsLifetime?: InitialCustomInstructionsLifetime;
  preSessionVariant?: PreSessionVariant;
  analyticsSurface?: SessionAnalyticsSurface;
  embedAnalyticsEventPrefix?: string;
  enableFreemiumPreSessionOffer?: boolean;
  prewarmPolicy?: SessionPrewarmPolicy;
  launchSurface?: SessionLaunchSurface;
  preferenceStorage?: SessionPreferenceStorage;
  shellLayout?: AppShellLayout;
  enableWebappSignupBeforeDemoExperiment?: boolean;
  showGooglePlayBadge?: boolean;
}

const MainAppView: React.FC<MainAppViewProps> = ({
  children,
  brand = DEFAULT_APP_BRAND,
  segments,
  currentTime,
  currentSegment,
  showTranslation,
  isPlaying,
  togglePlay,
  onSegmentClick,
  onExplainSegment,
  setShowTranslation,
  getSegmentClasses,
  setInteractionMode,
  initialTargetLanguageParam,
  initialInstructionLanguageParam,
  initialLanguageLevelParam,
  initialSessionModeParam,
  initialJourneyChapterIdParam,
  initialPodcastDiscussionIdParam,
  initialCustomInstructionsParam,
  initialCustomInstructionsLifetime = "persistent",
  preSessionVariant = "default",
  analyticsSurface = "fullscreen",
  embedAnalyticsEventPrefix,
  enableFreemiumPreSessionOffer = true,
  prewarmPolicy = "normal",
  launchSurface = "standard",
  preferenceStorage = "persistent",
  shellLayout = "fullscreen",
  enableWebappSignupBeforeDemoExperiment = false,
  showGooglePlayBadge = false,
}) => {
  const { skipForward, skipBackward } = usePlayer();
  const posthog = usePostHog();
  const router = useRouter();

  // State for view options
  const [showTranscript, setShowTranscript] = useState<boolean>(false);
  const [isAndroidBrowser, setIsAndroidBrowser] = useState(false);

  useEffect(() => {
    setIsAndroidBrowser(getPlatform() === "android");
  }, []);

  // State for session mode
  const [sessionMode, setSessionMode] = useState<SessionMode | null>(null);
  const [conversationStartChoice, setConversationStartChoiceState] =
    useState<ConversationStartChoice>("new");
  const [activeContinuation, setActiveContinuation] = useState<{
    conversation: Conversation;
    sourceConversationId: string;
    previousTutorMessages: string[];
  } | null>(null);
  const [pendingSessionLaunch, setPendingSessionLaunch] = useState<{
    nonce: number;
    mode?: SessionMode;
  } | null>(null);
  const pendingSessionLaunchResolverRef = useRef<((started: boolean) => void) | null>(null);
  const [tutorWhiteboardEnabled, setTutorWhiteboardEnabled] = useState(false);
  const [journeyChapterId, setJourneyChapterId] = useState<string | null>(null);
  const [journeyCoachingInstructions, setJourneyCoachingInstructions] = useState<string | null>(
    null
  );
  const [podcastDiscussionId, setPodcastDiscussionId] = useState<string | null>(null);
  const [isPodcastDiscussionEnabled, setIsPodcastDiscussionEnabled] = useState(true);
  const [sessionVocabularyContext, setSessionVocabularyContext] =
    useState<SessionVocabularyContext | null>(() => loadSessionVocabularySelection());

  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = window.localStorage.getItem(CONVERSATION_START_CHOICE_STORAGE_KEY);
    if (saved === "new" || saved === "continue_last") setConversationStartChoiceState(saved);
  }, []);

  const setConversationStartChoice = useCallback((choice: ConversationStartChoice) => {
    setConversationStartChoiceState(choice);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(CONVERSATION_START_CHOICE_STORAGE_KEY, choice);
    }
  }, []);

  // State for selected model
  const [selectedModel, setSelectedModel] = useState<"pro" | "stealth">("pro");
  const [freemiumPreSessionOfferDismissSignal, setFreemiumPreSessionOfferDismissSignal] =
    useState(0);
  const [webappDemoReadyAfterAuth, setWebappDemoReadyAfterAuth] = useState(false);
  const hasTrackedWebappDemoAuthReturnRef = useRef(false);
  const [initialLaunchContextReady, setInitialLaunchContextReady] = useState(
    preSessionVariant === "default"
  );

  useEffect(() => {
    const refreshSessionVocabularySelection = () => {
      setSessionVocabularyContext(loadSessionVocabularySelection());
    };

    window.addEventListener("focus", refreshSessionVocabularySelection);
    window.addEventListener("storage", refreshSessionVocabularySelection);
    window.addEventListener(SESSION_VOCABULARY_SELECTION_EVENT, refreshSessionVocabularySelection);
    return () => {
      window.removeEventListener("focus", refreshSessionVocabularySelection);
      window.removeEventListener("storage", refreshSessionVocabularySelection);
      window.removeEventListener(
        SESSION_VOCABULARY_SELECTION_EVENT,
        refreshSessionVocabularySelection
      );
    };
  }, []);

  // State for canvas visibility
  const [isCanvasVisible, setIsCanvasVisible] = useState<boolean>(false);
  // Class enrollment state
  const {
    enrolledClasses,
    selectedClassId,
    setSelectedClassId,
    joinClass,
    getClassPreferences,
    isLoading,
    error,
  } = useClassEnrollment();
  const [isClassroomModalOpen, setIsClassroomModalOpen] = useState(false);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
  const [teacherPreferences, setTeacherPreferences] = useState<any>(null);
  const [languageClassWarning, setLanguageClassWarning] = useState<string | null>(null);
  const [currentPracticeSetupStep, setCurrentPracticeSetupStep] = useState<"language" | "manual">(
    "language"
  );

  // Use localStorage-backed preferences instead of regular useState
  const {
    preferences,
    isInitialized, // Added isInitialized
    setLanguageLevel,
    setVoice,
    setTeachingStyle,
    setNickname,
    setCustomInstructions,
    setNoAvatar,
    setPreferences,
    commitLanguagePreferencesToStorage,
  } = useUserPreferences({ storage: preferenceStorage });

  // Destructure preferences for easier access
  const {
    selectedLanguageCode,
    nativeLanguageCode,
    lastGuidedInstructionLanguageCode,
    translationLanguageCode: preferredTranslationLanguageCode,
    languageLevel,
    voice,
    teachingStyle,
    nickname,
    customInstructions,
    noAvatar,
  } = preferences;
  const {
    effectiveCustomInstructions,
    shouldClearSavedCustomInstructions,
    shouldPersistInitialCustomInstructions,
  } = useInitialCustomInstructions({
    initialCustomInstructions: initialCustomInstructionsParam,
    savedCustomInstructions: customInstructions,
    lifetime: initialCustomInstructionsLifetime,
  });
  const sessionCustomInstructions = capCustomInstructionsInput(
    sessionMode === "journey" && journeyCoachingInstructions
      ? [journeyCoachingInstructions, effectiveCustomInstructions].filter(Boolean).join("\n")
      : effectiveCustomInstructions
  );
  const { user, isLoading: authLoading } = useAuth(); // Get user from AuthContext
  const isAuthenticated = Boolean(user?.id);
  const isGuestUser = !authLoading && !isAuthenticated;
  const isWebappSignupBeforeDemoEligible =
    enableWebappSignupBeforeDemoExperiment &&
    analyticsSurface === "fullscreen" &&
    launchSurface === "standard" &&
    preSessionVariant === "default" &&
    shellLayout === "fullscreen" &&
    isGuestUser &&
    Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY);
  const webappSignupBeforeDemoExperiment = useWebappSignupBeforeDemoExperiment({
    enabled: isWebappSignupBeforeDemoEligible,
    posthog,
  });
  const isWebappSignupBeforeDemoTreatment =
    webappSignupBeforeDemoExperiment.status === "resolved" &&
    webappSignupBeforeDemoExperiment.source === "posthog" &&
    webappSignupBeforeDemoExperiment.variant === WEBAPP_SIGNUP_BEFORE_DEMO_VARIANTS.signupFirst;
  const effectiveTutorWhiteboardEnabled = isAuthenticated && tutorWhiteboardEnabled;
  const isSpatiusAvatarProvider =
    (process.env.NEXT_PUBLIC_AVATAR_PROVIDER || "anam").trim().toLowerCase() === "spatius";
  const guestSpatiusAvatarExperiment = useGuestSpatiusAvatarExperiment({
    enabled:
      analyticsSurface === "fullscreen" &&
      isGuestUser &&
      isSpatiusAvatarProvider &&
      Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY),
    posthog,
  });
  const guestSpatiusAvatarTreatmentEnabled =
    guestSpatiusAvatarExperiment.status === "resolved" &&
    guestSpatiusAvatarExperiment.source === "posthog" &&
    guestSpatiusAvatarExperiment.variant === GUEST_SPATIUS_AVATAR_VARIANTS.spatiusAvatar;
  const isAvatarForcedOff = isGuestUser && !guestSpatiusAvatarTreatmentEnabled;
  const effectiveNoAvatar = isAvatarForcedOff || noAvatar;
  const hasSelectedLanguage = Boolean(selectedLanguageCode);

  useEffect(() => {
    if (!isAuthenticated) {
      setTutorWhiteboardEnabled(false);
    }
  }, [isAuthenticated]);

  useSpatiusSdkPreinit(
    !authLoading && !effectiveNoAvatar && !(isGuestUser && isWebappSignupBeforeDemoTreatment)
  );

  const exposeGuestSpatiusAvatar = useCallback(() => {
    if (
      !posthog ||
      guestSpatiusAvatarExperiment.status !== "resolved" ||
      guestSpatiusAvatarExperiment.source !== "posthog"
    ) {
      return null;
    }

    return exposeGuestSpatiusAvatarExperiment(posthog, guestSpatiusAvatarExperiment.variant);
  }, [guestSpatiusAvatarExperiment, posthog]);

  const selectedLanguageName =
    Object.entries(languagesData).find(([, code]) => code === selectedLanguageCode)?.[0] ||
    "English";
  const resolvedInitialTargetLanguageCode = resolveLanguageCodeFromParam(
    initialTargetLanguageParam,
    languagesData
  );
  const resolvedInitialLanguageLevel = resolveLanguageLevelFromParam(initialLanguageLevelParam);
  const routeProvidedPracticeSetupStep =
    resolvedInitialTargetLanguageCode && resolvedInitialTargetLanguageCode === selectedLanguageCode
      ? "manual"
      : undefined;
  const hasAppliedInitialLanguagePairRef = useRef(false);
  const hasAppliedInitialSessionContextRef = useRef(false);
  const hasAppliedInitialCustomInstructionsRef = useRef(false);

  useEffect(() => {
    if (!isInitialized || !shouldClearSavedCustomInstructions) {
      return;
    }

    setCustomInstructions("");
  }, [isInitialized, setCustomInstructions, shouldClearSavedCustomInstructions]);

  useEffect(() => {
    const defaultChapter = getDefaultLanguageJourneyChapter(selectedLanguageCode);
    setJourneyChapterId((current) => {
      if (current && initialJourneyChapterIdParam === current) return current;
      if (!defaultChapter) return null;
      return current && current.startsWith(`${defaultChapter.id.split("-")[0]}-`)
        ? current
        : defaultChapter.id;
    });
  }, [initialJourneyChapterIdParam, selectedLanguageCode]);

  useEffect(() => {
    if (!isInitialized || hasAppliedInitialLanguagePairRef.current) {
      return;
    }

    const resolvedTargetCode = resolvedInitialTargetLanguageCode;
    const resolvedInstructionCode = resolveLanguageCodeFromParam(
      initialInstructionLanguageParam,
      languagesData
    );

    const defaultInstructionCode = resolvedTargetCode
      ? getDefaultInstructionLanguageCodeForTarget(resolvedTargetCode)
      : null;
    const nextInstructionCode = resolvedInstructionCode || defaultInstructionCode;

    /**
     * Important UX rule:
     * - Page params (SEO pages like /ai/[slug], /practice/[slug], /learn/[target],
     *   /learn/[target]/from/[source]) should apply once when the page opens, but must
     *   not keep overwriting the user's changes after mount. Otherwise the target
     *   language feels "locked".
     */
    const shouldApplyTarget = Boolean(
      resolvedTargetCode && resolvedTargetCode !== selectedLanguageCode
    );
    const shouldApplyInstruction = Boolean(
      nextInstructionCode && nextInstructionCode !== nativeLanguageCode
    );
    const shouldApplyLanguageLevel = Boolean(
      resolvedInitialLanguageLevel && resolvedInitialLanguageLevel !== languageLevel
    );

    if (shouldApplyTarget || shouldApplyInstruction || shouldApplyLanguageLevel) {
      setPreferences((prev) => ({
        ...prev,
        ...(shouldApplyTarget ? { selectedLanguageCode: resolvedTargetCode! } : {}),
        ...(shouldApplyLanguageLevel ? { languageLevel: resolvedInitialLanguageLevel! } : {}),
        ...(shouldApplyInstruction
          ? {
              nativeLanguageCode: nextInstructionCode!,
              ...(!hasSameBaseLanguage(
                resolvedTargetCode || prev.selectedLanguageCode,
                nextInstructionCode
              )
                ? { lastGuidedInstructionLanguageCode: nextInstructionCode! }
                : {}),
            }
          : {}),
      }));
    }

    hasAppliedInitialLanguagePairRef.current = true;
    setInitialLaunchContextReady(true);
  }, [
    initialInstructionLanguageParam,
    isInitialized,
    languageLevel,
    nativeLanguageCode,
    resolvedInitialLanguageLevel,
    resolvedInitialTargetLanguageCode,
    selectedLanguageCode,
    setPreferences,
  ]);

  useEffect(() => {
    if (!isInitialized || hasAppliedInitialSessionContextRef.current) {
      return;
    }

    const parsedSessionMode = parseSessionMode(initialSessionModeParam);
    if (parsedSessionMode && parsedSessionMode !== "visual") {
      setSessionMode(parsedSessionMode);
    }
    if (initialJourneyChapterIdParam) {
      setJourneyChapterId(initialJourneyChapterIdParam);
      if (!parsedSessionMode) {
        setSessionMode("journey");
      }
    }
    if (initialPodcastDiscussionIdParam) {
      setPodcastDiscussionId(initialPodcastDiscussionIdParam);
      setIsPodcastDiscussionEnabled(true);
      setSessionMode("journey");
    }

    hasAppliedInitialSessionContextRef.current = true;
  }, [
    initialJourneyChapterIdParam,
    initialPodcastDiscussionIdParam,
    initialSessionModeParam,
    isInitialized,
  ]);

  useEffect(() => {
    if (
      !isInitialized ||
      hasAppliedInitialCustomInstructionsRef.current ||
      !shouldPersistInitialCustomInstructions
    ) {
      return;
    }

    const nextCustomInstructions = initialCustomInstructionsParam?.trim();
    if (nextCustomInstructions && nextCustomInstructions !== customInstructions) {
      setCustomInstructions(nextCustomInstructions);
    }

    hasAppliedInitialCustomInstructionsRef.current = true;
  }, [
    customInstructions,
    initialCustomInstructionsParam,
    isInitialized,
    setCustomInstructions,
    shouldPersistInitialCustomInstructions,
  ]);

  // Handler for class selection with language validation
  const handleClassSelection = useCallback(
    (classId: string | null) => {
      setSelectedClassId(classId);
      setLanguageClassWarning(null);

      if (classId && selectedLanguageCode) {
        const selectedClass = enrolledClasses.find((c) => c.class_id === classId);
        if (selectedClass) {
          // Check if selected language matches class target language
          const classLanguageName = selectedClass.language_target;
          if (
            !selectedLanguageName.toLowerCase().includes(classLanguageName.toLowerCase()) &&
            !classLanguageName.toLowerCase().includes(selectedLanguageName.toLowerCase())
          ) {
            setLanguageClassWarning(
              `⚠️ You selected ${selectedLanguageName} but joined "${selectedClass.class_name}" which focuses on ${classLanguageName}. You may not get ${selectedLanguageName} scenarios.`
            );
          }
        }
      }
    },
    [selectedLanguageCode, selectedLanguageName, enrolledClasses, setSelectedClassId]
  );

  // Handler for language selection with class validation
  const handleLanguageSelection = useCallback(
    (languageCode: string) => {
      if (preSessionVariant === "configurable-start") {
        setPreferences((prev) => ({
          ...prev,
          selectedLanguageCode: languageCode,
          ...(languageCode !== prev.selectedLanguageCode ? { customInstructions: "" } : {}),
        }));
        setLanguageClassWarning(null);
        return;
      }

      const defaultInstructionCode = getDefaultInstructionLanguageCodeForTarget(languageCode);
      setPreferences((prev) => ({
        ...prev,
        selectedLanguageCode: languageCode,
        nativeLanguageCode: defaultInstructionCode,
        lastGuidedInstructionLanguageCode:
          defaultInstructionCode !== languageCode
            ? defaultInstructionCode
            : prev.lastGuidedInstructionLanguageCode,
      }));
      setLanguageClassWarning(null);

      if (selectedClassId && languageCode) {
        const selectedClass = enrolledClasses.find((c) => c.class_id === selectedClassId);
        if (selectedClass) {
          const newLanguageName =
            Object.entries(languagesData).find(([, code]) => code === languageCode)?.[0] ||
            "English";
          const classLanguageName = selectedClass.language_target;
          if (
            !newLanguageName.toLowerCase().includes(classLanguageName.toLowerCase()) &&
            !classLanguageName.toLowerCase().includes(newLanguageName.toLowerCase())
          ) {
            setLanguageClassWarning(
              `⚠️ You selected ${newLanguageName} but are in "${selectedClass.class_name}" which focuses on ${classLanguageName}. You may not get ${newLanguageName} scenarios.`
            );
          }
        }
      }
    },
    [preSessionVariant, selectedClassId, enrolledClasses, setPreferences]
  );

  const handleInstructionLanguageSelection = useCallback(
    (instructionLanguageCode: string) => {
      setPreferences((prev) => ({
        ...prev,
        nativeLanguageCode: hasSameBaseLanguage(prev.selectedLanguageCode, instructionLanguageCode)
          ? prev.selectedLanguageCode
          : instructionLanguageCode,
        ...(!hasSameBaseLanguage(prev.selectedLanguageCode, instructionLanguageCode)
          ? { lastGuidedInstructionLanguageCode: instructionLanguageCode }
          : {}),
      }));
    },
    [setPreferences]
  );

  const guidedInstructionLanguageCode = React.useMemo(() => {
    if (!selectedLanguageCode) {
      return (
        lastGuidedInstructionLanguageCode || nativeLanguageCode || ENGLISH_INSTRUCTION_LANGUAGE_CODE
      );
    }

    return getGuidedInstructionLanguageCode({
      targetLanguageCode: selectedLanguageCode,
      rememberedInstructionLanguageCode: lastGuidedInstructionLanguageCode,
      currentInstructionLanguageCode: nativeLanguageCode,
      fallbackInstructionLanguageCode:
        getDefaultInstructionLanguageCodeForTarget(selectedLanguageCode),
    });
  }, [lastGuidedInstructionLanguageCode, nativeLanguageCode, selectedLanguageCode]);

  const translationLanguageCode = resolveTranslationLanguageCode({
    targetLanguageCode: selectedLanguageCode,
    preferredLanguageCode: preferredTranslationLanguageCode,
    guidedInstructionLanguageCode,
  });
  const handleTranslationLanguageChange = useCallback(
    (code: string) =>
      setPreferences((previous) => ({ ...previous, translationLanguageCode: code })),
    [setPreferences]
  );

  const learningMode = React.useMemo(
    () => deriveStandardLearningMode(selectedLanguageCode, nativeLanguageCode),
    [nativeLanguageCode, selectedLanguageCode]
  );

  useEffect(() => {
    if (
      sessionMode === "journey" ||
      !selectedLanguageCode ||
      nativeLanguageCode === selectedLanguageCode ||
      !hasSameBaseLanguage(selectedLanguageCode, nativeLanguageCode)
    ) {
      return;
    }

    setPreferences((previous) => ({
      ...previous,
      nativeLanguageCode: selectedLanguageCode,
    }));
  }, [nativeLanguageCode, selectedLanguageCode, sessionMode, setPreferences]);

  // Derive nativeLanguageName from preferences
  const nativeLanguageName = React.useMemo(() => {
    return Object.entries(languagesData).find(([, code]) => code === nativeLanguageCode)?.[0] || "";
  }, [nativeLanguageCode]);

  const { subscribed: resolvedSubscriptionAccess, managementUrl } = useRevenueCatSubscription(
    user?.email || null
  );
  const subscribed = hasVerifiedRevenueCatAccess(resolvedSubscriptionAccess);

  const { userLevel, loading: userLevelLoading, refreshLevel } = useUserLevel(user?.email || null);

  const { streakDisplay, loading: streakLoading, updateStreak } = useStreak(user?.id || null);

  const handleFreemiumPreSessionOfferDismissed = useCallback(() => {
    setFreemiumPreSessionOfferDismissSignal((signal) => signal + 1);
  }, []);

  const {
    room,
    isConnected,
    isRecording,
    recordingTime,
    connectionElapsedSeconds,
    agentState,
    connectToLiveKit,
    disconnectFromLiveKit,
    toggleRecording,
    showTimeLimitPopup,
    dismissTimeLimitPopup,
    timeLimitPopupContext,
    freemiumUsageState,
    showFreemiumPreSessionOffer,
    dismissFreemiumPreSessionOffer,
    continueAfterFreemiumPreSessionOffer,
    hasExceededGuestLimit,
    connectionState,
    connectionError,
    retryConnection,
    audioTrackError,
    audioRetryCount,
    retryAudioTrack,
    clearAudioErrors,
    baseRoom,
    spatiusPrimarySession,
    handleSpatiusPrimaryConnected,
    handleSpatiusPrimaryDisconnected,
    handleSpatiusPrimaryError,
    handleSpatiusPrimaryStateChange,
  } = useLiveKitAgent({
    selectedLanguageCode,
    selectedLanguageLevel: languageLevel,
    selectedVoice: voice,
    selectedTeachingStyle: teachingStyle,
    nickname,
    customInstructions: sessionCustomInstructions,
    noAvatar: effectiveNoAvatar,
    allowUnauthenticatedSessions: true,
    guestSessionLimitSeconds: FULLSCREEN_GUEST_SESSION_SECONDS,
    fromLanguageName: nativeLanguageName, // Pass native language name
    fromLanguageCode: nativeLanguageCode, // Pass native language code
    learningMode: !sessionMode || sessionMode === "conversation" ? learningMode : undefined,
    sessionMode: sessionMode || undefined,
    tutorWhiteboardEnabled: effectiveTutorWhiteboardEnabled,
    setInteractionMode,
    subscribed: resolvedSubscriptionAccess,
    classId: selectedClassId || undefined,
    teacherPreferences, // Pass teacher preferences for personalized experience
    selectedModel, // Pass selected model
    journeyChapterId: sessionMode === "journey" ? journeyChapterId : null,
    podcastDiscussionId: isPodcastDiscussionEnabled ? podcastDiscussionId : null,
    sessionVocabularyContext,
    conversationId: activeContinuation?.conversation.id || null,
    resumeFromConversationId: activeContinuation?.sourceConversationId || null,
    analyticsSurface,
    embedAnalyticsEventPrefix,
    enableFreemiumPreSessionOffer,
    launchSurface,
    prewarmPolicy,
    getGuestSpatiusAvatarExperimentExposure: exposeGuestSpatiusAvatar,
    webappSignupBeforeDemoExperimentEligible: enableWebappSignupBeforeDemoExperiment,
    getWebappSignupBeforeDemoExperimentProperties: getWebappSignupBeforeDemoAttributionProperties,
    onFreemiumPreSessionOfferDismissed: handleFreemiumPreSessionOfferDismissed,
  });

  useEffect(() => {
    if (
      !enableWebappSignupBeforeDemoExperiment ||
      !user ||
      hasTrackedWebappDemoAuthReturnRef.current ||
      typeof window === "undefined"
    ) {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get(WEBAPP_SIGNUP_BEFORE_DEMO_READY_QUERY_KEY) !== "ready") return;

    const restoredExposure = restoreWebappSignupBeforeDemoExposureFromSearch({
      search: window.location.search,
    });
    if (
      restoredExposure?.properties.experiment_variant !==
      WEBAPP_SIGNUP_BEFORE_DEMO_VARIANTS.signupFirst
    ) {
      return;
    }

    hasTrackedWebappDemoAuthReturnRef.current = true;
    setWebappDemoReadyAfterAuth(true);
    posthog?.capture("webapp_signup_before_demo_auth_completed", {
      auth_user_id: user.id,
      ...getWebappSignupBeforeDemoAttributionProperties(),
    });

    const cleanedUrl = new URL(window.location.href);
    cleanedUrl.searchParams.delete(WEBAPP_SIGNUP_BEFORE_DEMO_READY_QUERY_KEY);
    cleanedUrl.searchParams.delete(WEBAPP_SIGNUP_BEFORE_DEMO_QUERY_KEY);
    window.history.replaceState(
      {},
      "",
      `${cleanedUrl.pathname}${cleanedUrl.search}${cleanedUrl.hash}`
    );
  }, [enableWebappSignupBeforeDemoExperiment, posthog, user]);

  const handleToggleRecordingBase = useCallback(
    async (requestedSessionMode?: SessionMode): Promise<boolean> => {
      if (isRecording) {
        return toggleRecording(requestedSessionMode);
      }

      if (user && webappDemoReadyAfterAuth) {
        posthog?.capture("webapp_signup_before_demo_demo_start_clicked", {
          selected_language_code: selectedLanguageCode,
          selected_language_level: languageLevel,
          session_mode: requestedSessionMode || sessionMode || "conversation",
          ...getWebappSignupBeforeDemoAttributionProperties(),
        });
        const started = await toggleRecording(requestedSessionMode);
        if (started) setWebappDemoReadyAfterAuth(false);
        return started;
      }

      if (
        !posthog ||
        !isWebappSignupBeforeDemoEligible ||
        webappSignupBeforeDemoExperiment.status !== "resolved" ||
        webappSignupBeforeDemoExperiment.source !== "posthog"
      ) {
        return toggleRecording(requestedSessionMode);
      }

      const exposure = exposeWebappSignupBeforeDemoExperiment(
        posthog,
        webappSignupBeforeDemoExperiment.variant
      );
      if (!exposure) return toggleRecording(requestedSessionMode);

      const attributionProperties = getWebappSignupBeforeDemoAttributionProperties();
      if (exposure.newlyExposed) {
        posthog.capture("webapp_signup_before_demo_exposed", {
          ...exposure.properties,
          analytics_surface: analyticsSurface,
          selected_language_code: selectedLanguageCode,
          selected_language_level: languageLevel,
          session_mode: requestedSessionMode || sessionMode || "conversation",
        });
      }

      if (webappSignupBeforeDemoExperiment.variant === WEBAPP_SIGNUP_BEFORE_DEMO_VARIANTS.control) {
        return toggleRecording(requestedSessionMode);
      }

      posthog.capture("webapp_signup_before_demo_auth_started", {
        auth_position: "before_demo",
        ...attributionProperties,
      });
      const currentPath = typeof window === "undefined" ? "/" : window.location.pathname;
      const currentSearch = typeof window === "undefined" ? "" : window.location.search;
      router.push(
        buildWebappSignupBeforeDemoAuthUrl({
          currentPath,
          currentSearch,
          variant: webappSignupBeforeDemoExperiment.variant,
        })
      );
      return false;
    },
    [
      analyticsSurface,
      isRecording,
      isWebappSignupBeforeDemoEligible,
      languageLevel,
      posthog,
      router,
      selectedLanguageCode,
      sessionMode,
      toggleRecording,
      user,
      webappDemoReadyAfterAuth,
      webappSignupBeforeDemoExperiment.source,
      webappSignupBeforeDemoExperiment.status,
      webappSignupBeforeDemoExperiment.variant,
    ]
  );

  useEffect(() => {
    if (!pendingSessionLaunch) return;
    const pending = pendingSessionLaunch;
    setPendingSessionLaunch(null);

    void handleToggleRecordingBase(pending.mode).then(async (started) => {
      if (!started && activeContinuation?.conversation.id) {
        try {
          await deleteConversationAPI(activeContinuation.conversation.id);
        } catch {
          // Best-effort cleanup; the empty child remains harmless and can be deleted from history.
        }
        setActiveContinuation(null);
        posthog?.capture("conversation_continuation_connection_failed", {
          source_surface: "launch",
        });
      } else if (started && activeContinuation) {
        posthog?.capture("conversation_continuation_connected", {
          source_conversation_id: activeContinuation.sourceConversationId,
          conversation_id: activeContinuation.conversation.id,
        });
      }
      pendingSessionLaunchResolverRef.current?.(started);
      pendingSessionLaunchResolverRef.current = null;
    });
  }, [activeContinuation, handleToggleRecordingBase, pendingSessionLaunch, posthog]);

  const queueSessionLaunch = useCallback(
    (
      continuation: typeof activeContinuation,
      requestedSessionMode?: SessionMode
    ): Promise<boolean> => {
      setActiveContinuation(continuation);
      return new Promise<boolean>((resolve) => {
        pendingSessionLaunchResolverRef.current = resolve;
        setPendingSessionLaunch({ nonce: Date.now(), mode: requestedSessionMode });
      });
    },
    []
  );

  const resolveContinuation = useCallback(
    async (
      sourceSurface: "history" | "settings",
      sourceConversationId?: string
    ): Promise<typeof activeContinuation> => {
      const result: ContinueConversationResult = await continueConversation(
        sourceConversationId
          ? { sourceSurface, sourceConversationId }
          : {
              sourceSurface,
              languageName: selectedLanguageName,
              languageLevel,
            }
      );
      if (
        !result.continued ||
        !result.conversation ||
        !result.sourceConversationId ||
        !result.previousTutorMessages
      ) {
        posthog?.capture("conversation_continuation_fallback", {
          source_surface: sourceSurface,
          selected_language: selectedLanguageName,
        });
        return null;
      }
      posthog?.capture("conversation_continuation_resolved", {
        source_surface: sourceSurface,
        selection: sourceConversationId ? "exact" : "latest_language",
        source_conversation_id: result.sourceConversationId,
        conversation_id: result.conversation.id,
      });
      return {
        conversation: result.conversation,
        sourceConversationId: result.sourceConversationId,
        previousTutorMessages: result.previousTutorMessages,
      };
    },
    [languageLevel, posthog, selectedLanguageName]
  );

  const handleToggleRecording = useCallback(
    async (requestedSessionMode?: SessionMode): Promise<boolean> => {
      if (isRecording) return handleToggleRecordingBase(requestedSessionMode);

      let continuation: typeof activeContinuation = null;
      if (
        subscribed &&
        conversationStartChoice === "continue_last" &&
        isWebConversationContinuationEnabled()
      ) {
        try {
          continuation = await resolveContinuation("settings");
        } catch (error) {
          console.warn("Unable to resolve the previous speaking session; starting new", error);
          posthog?.capture("conversation_continuation_failed", { source_surface: "settings" });
        }
      }
      return queueSessionLaunch(continuation, requestedSessionMode);
    },
    [
      conversationStartChoice,
      handleToggleRecordingBase,
      isRecording,
      posthog,
      queueSessionLaunch,
      resolveContinuation,
      subscribed,
    ]
  );

  // Prewarm on language selection or mode change
  useEffect(() => {
    const effectivePodcastDiscussionId = isPodcastDiscussionEnabled ? podcastDiscussionId : null;
    if (
      !shouldPrewarm({
        authLoading,
        enabled: PREWARM_ENABLED,
        mode:
          launchSurface === "acquisition-demo" || prewarmPolicy === "fresh-on-start"
            ? "acquisition-fresh-on-start"
            : "standard",
        podcastDiscussionId: effectivePodcastDiscussionId,
        selectedLanguageCode,
      }) ||
      (isGuestUser && guestSpatiusAvatarExperiment.status === "loading") ||
      (isWebappSignupBeforeDemoEligible &&
        (webappSignupBeforeDemoExperiment.status === "loading" ||
          isWebappSignupBeforeDemoTreatment))
    ) {
      return;
    }
    const controller = new AbortController();
    const prewarm = async () => {
      try {
        const clientId = getOrCreateClientId();
        const {
          data: { session: authSession },
        } = await supabase.auth.getSession();
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (authSession?.access_token) {
          headers.Authorization = `Bearer ${authSession.access_token}`;
        }
        await fetch("/api/prewarm-token", {
          method: "POST",
          headers,
          body: JSON.stringify({
            clientId,
            languageCode: selectedLanguageCode,
            languageName: selectedLanguageName,
            userEmail: user?.email || null,
            userId: user?.id || null,
            sessionMode: sessionMode || "conversation",
            learningMode: !sessionMode || sessionMode === "conversation" ? learningMode : undefined,
            tutorWhiteboardEnabled: effectiveTutorWhiteboardEnabled,
            fromLanguageCode: nativeLanguageCode || null,
            fromLanguageName: nativeLanguageName || null,
            voice,
            languageLevel,
            teachingStyle,
            nickname,
            customInstructions: sessionCustomInstructions,
            model: selectedModel,
            noAvatar: effectiveNoAvatar,
            journeyChapterId: sessionMode === "journey" ? journeyChapterId : null,
            podcastDiscussionId: effectivePodcastDiscussionId,
            vocabularyContext: sessionVocabularyContext,
          }),
          signal: controller.signal,
        });
      } catch (e) {
        // Silent fail
      }
    };
    prewarm();
    return () => controller.abort();
  }, [
    selectedLanguageCode,
    selectedLanguageName,
    nativeLanguageCode,
    nativeLanguageName,
    learningMode,
    sessionMode,
    effectiveTutorWhiteboardEnabled,
    user?.email,
    user?.id,
    authLoading,
    voice,
    languageLevel,
    teachingStyle,
    nickname,
    sessionCustomInstructions,
    selectedModel,
    effectiveNoAvatar,
    journeyChapterId,
    isPodcastDiscussionEnabled,
    podcastDiscussionId,
    sessionVocabularyContext,
    launchSurface,
    prewarmPolicy,
    isGuestUser,
    guestSpatiusAvatarExperiment.status,
    isWebappSignupBeforeDemoEligible,
    isWebappSignupBeforeDemoTreatment,
    webappSignupBeforeDemoExperiment.status,
  ]);

  // Fetch teacher preferences when a class is selected
  useEffect(() => {
    if (!selectedClassId) {
      setTeacherPreferences(null);
      return;
    }

    const fetchPreferences = async () => {
      try {
        const preferences = await getClassPreferences(selectedClassId);
        setTeacherPreferences(preferences);
      } catch (error) {
        console.error("Failed to fetch teacher preferences:", error);
        setTeacherPreferences(null);
      }
    };

    fetchPreferences();
  }, [selectedClassId, getClassPreferences]);

  const handleDisconnect = () => {
    if (
      sessionMode === "journey" &&
      selectedLanguageCode &&
      journeyChapterId &&
      recordingTime >= JOURNEY_AUTO_COMPLETE_SECONDS
    ) {
      markLanguageJourneyChapterComplete(selectedLanguageCode, journeyChapterId, user?.id);
      if (user?.id) {
        void syncLanguageJourneyProgress(selectedLanguageCode, user.id).catch(() => {
          // The identity-scoped local completion remains queued for the next sync.
        });
      }
    }
    disconnectFromLiveKit(false, false, "fullscreen_manual_disconnect");
    setSessionMode(null);
    // After a session, trigger a refresh of the user's level.
    // A delay is included in the hook to allow memories to be processed first.
    refreshLevel();
  };

  // Pass disconnectFromLiveKit to handle Esc key for stopping agent, only if recording
  useKeyboardShortcuts({
    togglePlay: isRecording ? undefined : togglePlay, // Only allow space to toggle play if not recording
    skipBackward: isRecording ? undefined : skipBackward,
    skipForward: isRecording ? undefined : skipForward,
    stopAgent: isRecording ? handleDisconnect : undefined,
  });

  // Add navigation state
  const [activeTab, setActiveTab] = useState<
    "discover" | "listen" | "practice" | "progress" | "account"
  >("listen");
  // State for segment to explain in practice mode
  const [segmentToExplain, setSegmentToExplain] = useState<TranscriptSegment | undefined>(
    undefined
  );
  // NEW: State for the "from" language specifically for the practice view
  const [practiceFromLanguageName, setPracticeFromLanguageName] = useState<string | undefined>(
    undefined
  );
  // State to force new conversation in practice mode
  const [forceNewConversation, setForceNewConversation] = useState<boolean>(false);
  const [practiceEntryPoint, setPracticeEntryPoint] = useState<"writing" | "history">("writing");
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false); // State for about modal
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false); // Feedback modal
  const [isPastConversationsUpgradeModalOpen, setIsPastConversationsUpgradeModalOpen] =
    useState(false); // Upgrade modal for past conversations
  const [isLeaderboardVisible, setIsLeaderboardVisible] = useState(false);
  const [isHeaderCondensed, setIsHeaderCondensed] = useState(false);
  const [lastDisconnectTime, setLastDisconnectTime] = useState<number>(0); // Track when conversations end

  // Handle help click
  const handleHelpClick = () => {
    setIsAboutModalOpen(true);
  };

  const handleLeaderboardClick = () => {
    setIsLeaderboardVisible(true);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash === "#practice-leaderboard") {
      setIsLeaderboardVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!isLeaderboardVisible) return;
    if (typeof window === "undefined") return;

    window.requestAnimationFrame(() => {
      const leaderboard = document.getElementById("practice-leaderboard");
      if (leaderboard) {
        leaderboard.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }, [isLeaderboardVisible]);

  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (latest) => {
    const nextValue = latest > 32;
    setIsHeaderCondensed((currentValue) => (currentValue === nextValue ? currentValue : nextValue));
  });

  // Handle classroom click
  const handleClassroomClick = () => {
    setIsClassroomModalOpen(true);
  };

  // Handle share click
  const handleShareClick = () => {
    if (typeof window === "undefined") return;

    const origin = `${window.location.origin}${brand.variant === "liceo" ? "/liceobritanico" : ""}`;

    if (navigator.share) {
      navigator
        .share({
          title: brand.shareTitle,
          text: brand.shareText,
          url: origin,
        })
        .catch((error) => console.log("Error sharing:", error));
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard
        .writeText(origin)
        .then(() => {
          alert("Link copied to clipboard!");
        })
        .catch(() => {
          // Final fallback
          prompt("Copy this link to share:", origin);
        });
    }
  };

  // Ensure all segments have speaker labels
  const segmentsWithSpeakers = assignSpeakersToSegments(segments);

  // Handle explain segment - Switch to practice tab and set segment to explain
  const handleExplainSegment = (segment: TranscriptSegment) => {
    // Switch to practice tab
    setActiveTab("practice");
    // Set the segment to be explained
    setSegmentToExplain(segment);
  };

  // NEW: Handle writing practice - just open the Practice tab without a specific segment
  // nativeLanguageName is now derived from useUserPreferences
  const handleStartWritingPractice = (
    explicitFromLanguageName?: string,
    forceNew: boolean = false
  ) => {
    // ListenView now handles setting the nativeLanguageCode in localStorage.
    // Fullscreen will derive nativeLanguageName from localStorage.
    setActiveTab("practice");
    setSegmentToExplain(undefined);
    // Use the explicitly passed name if available, otherwise fallback to the general nativeLanguageName from preferences
    setPracticeFromLanguageName(explicitFromLanguageName || nativeLanguageName);
    // Set whether to force a new conversation
    setForceNewConversation(forceNew);
    setPracticeEntryPoint("writing");
  };

  // Handle Past Conversations button click - check subscription
  const handlePastConversationsClick = () => {
    if (!user) return; // Should not happen as button is hidden for non-users

    if (subscribed) {
      setActiveTab("practice");
      setSegmentToExplain(undefined);
      setPracticeFromLanguageName(nativeLanguageName);
      setForceNewConversation(false);
      setPracticeEntryPoint("history");
    } else {
      // Show upgrade modal for free users
      setIsPastConversationsUpgradeModalOpen(true);
    }
  };

  const handleContinueConversation = useCallback(
    async (source: Conversation) => {
      try {
        const continuation = await resolveContinuation("history", source.id);
        if (!continuation) throw new Error("This conversation cannot be continued");

        const languageCode = Object.entries(languagesData).find(
          ([name]) =>
            name.toLocaleLowerCase() === continuation.conversation.languageName.toLocaleLowerCase()
        )?.[1];
        if (languageCode) {
          setPreferences((previous) => ({
            ...previous,
            selectedLanguageCode: languageCode,
          }));
        }
        setLanguageLevel(continuation.conversation.languageLevel);
        setActiveTab("listen");
        setShowTranscript(true);
        await queueSessionLaunch(continuation, sessionMode || undefined);
      } catch (error) {
        if (error instanceof ConversationAPIError && error.status === 403) {
          setIsPastConversationsUpgradeModalOpen(true);
          return;
        }

        console.error("Unable to continue conversation", error);
        posthog?.capture("conversation_continuation_failed", { source_surface: "history" });
        if (typeof window !== "undefined") {
          window.alert("We couldn't continue that session. Please try again.");
        }
      }
    },
    [
      posthog,
      queueSessionLaunch,
      resolveContinuation,
      sessionMode,
      setLanguageLevel,
      setPreferences,
    ]
  );

  // Handle closing practice chat - go back to listen tab
  const handleClosePracticeChat = () => {
    setActiveTab("listen");
    setSegmentToExplain(undefined);
    setPracticeFromLanguageName(undefined); // Reset the specific practice language
    setForceNewConversation(false); // Reset force new conversation flag
    setPracticeEntryPoint("writing");
  };

  // Add loading check - MOVED AFTER ALL HOOKS
  if (!isInitialized || authLoading || !initialLaunchContextReady) {
    return <SkeletonLoader brand={brand} />;
  }

  // Render the active tab content
  const renderContent = () => {
    switch (activeTab) {
      case "progress":
        return <ProgressView />;
      case "practice":
        return (
          <PracticeView
            segmentToExplain={segmentToExplain}
            languageName={selectedLanguageName}
            fromLanguageName={practiceFromLanguageName || nativeLanguageName} // Use specific practice language or fallback
            languageLevel={languageLevel}
            onClose={handleClosePracticeChat}
            forceNewConversation={forceNewConversation}
            initialSidebarOpen={practiceEntryPoint === "history"}
            onConversationActivity={updateStreak}
            onContinueConversation={
              isWebConversationContinuationEnabled() ? handleContinueConversation : undefined
            }
          />
        );
      case "account":
        return (
          <div className="pt-16 md:pt-[72px]">
            <AccountView />
          </div>
        );
      case "listen":
      default:
        return (
          <SpatiusPrimarySessionRoot
            baseRoom={baseRoom}
            onConnected={handleSpatiusPrimaryConnected}
            onDisconnected={handleSpatiusPrimaryDisconnected}
            onError={handleSpatiusPrimaryError}
            onStateChange={handleSpatiusPrimaryStateChange}
            session={spatiusPrimarySession}
          >
            <ListenView
              brandVariant={brand.variant}
              brandName={brand.name}
              segments={segmentsWithSpeakers}
              currentTime={currentTime}
              showTranslation={showTranslation}
              isPlaying={isPlaying}
              togglePlay={togglePlay}
              onSegmentClick={onSegmentClick}
              onExplainSegment={handleExplainSegment}
              getSegmentClasses={getSegmentClasses}
              isRecording={isRecording}
              isConnected={isConnected}
              room={room}
              disconnectFromLiveKit={handleDisconnect}
              toggleRecording={handleToggleRecording}
              formatRecordingTime={formatRecordingTime}
              recordingTime={recordingTime}
              connectionElapsedSeconds={connectionElapsedSeconds}
              showTranscript={showTranscript}
              setShowTranscript={setShowTranscript}
              selectedLanguageCode={selectedLanguageCode}
              setSelectedLanguageCode={handleLanguageSelection}
              nativeLanguageCode={nativeLanguageCode}
              setNativeLanguageCode={handleInstructionLanguageSelection}
              guidedInstructionLanguageCode={guidedInstructionLanguageCode}
              translationLanguageCode={translationLanguageCode}
              visualTranslationLanguageCode={resolveTranslationLanguageCode({
                targetLanguageCode: selectedLanguageCode,
                preferredLanguageCode: preferredTranslationLanguageCode,
              })}
              onTranslationLanguageChange={handleTranslationLanguageChange}
              journeyChapterId={journeyChapterId}
              setJourneyChapterId={setJourneyChapterId}
              podcastDiscussionId={podcastDiscussionId}
              isPodcastDiscussionEnabled={isPodcastDiscussionEnabled}
              setIsPodcastDiscussionEnabled={setIsPodcastDiscussionEnabled}
              selectedLanguageLevel={languageLevel}
              setSelectedLanguageLevel={setLanguageLevel}
              selectedVoice={voice}
              setSelectedVoice={setVoice}
              selectedTeachingStyle={teachingStyle}
              setSelectedTeachingStyle={setTeachingStyle}
              nickname={nickname}
              setNickname={setNickname}
              customInstructions={customInstructions}
              setCustomInstructions={setCustomInstructions}
              selectedNoAvatar={effectiveNoAvatar}
              setSelectedNoAvatar={setNoAvatar}
              isAvatarForcedOff={isAvatarForcedOff}
              showTimeLimitPopup={showTimeLimitPopup}
              dismissTimeLimitPopup={dismissTimeLimitPopup}
              timeLimitPopupContext={timeLimitPopupContext}
              hasExceededGuestLimit={hasExceededGuestLimit}
              connectionState={connectionState}
              connectionError={connectionError}
              retryConnection={retryConnection}
              audioTrackError={audioTrackError}
              audioRetryCount={audioRetryCount}
              retryAudioTrack={retryAudioTrack}
              clearAudioErrors={clearAudioErrors}
              sessionMode={sessionMode}
              onSessionModeChange={setSessionMode}
              isAuthenticated={isAuthenticated}
              userId={user?.id ?? null}
              tutorWhiteboardEnabled={effectiveTutorWhiteboardEnabled}
              onTutorWhiteboardEnabledChange={setTutorWhiteboardEnabled}
              onCanvasVisibilityChange={setIsCanvasVisible}
              startWritingPractice={handleStartWritingPractice}
              subscribed={subscribed}
              userEmail={user?.email || null}
              onConversationActivity={updateStreak}
              currentStreak={streakDisplay?.currentStreak ?? null}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              hasSpatiusAvatarSession={Boolean(spatiusPrimarySession)}
              freemiumPreSessionOfferDismissSignal={freemiumPreSessionOfferDismissSignal}
              initialPracticeSetupStep={routeProvidedPracticeSetupStep}
              preSessionVariant={preSessionVariant}
              analyticsSurface={analyticsSurface}
              embedAnalyticsEventPrefix={embedAnalyticsEventPrefix}
              onCommitLanguagePreferences={commitLanguagePreferencesToStorage}
              onJourneyCoachingInstructionsChange={setJourneyCoachingInstructions}
              shellLayout={shellLayout}
              conversationStartChoice={conversationStartChoice}
              setConversationStartChoice={setConversationStartChoice}
              continuationConversation={activeContinuation?.conversation || null}
              previousTutorMessages={activeContinuation?.previousTutorMessages || []}
              onPracticeSetupStepChange={setCurrentPracticeSetupStep}
            />
          </SpatiusPrimarySessionRoot>
        );
    }
  };

  const shouldShowImmersiveShellChrome =
    shellLayout !== "inline" &&
    preSessionVariant === "default" &&
    (activeTab === "listen" || activeTab === "account") &&
    !isCanvasVisible &&
    !isRecording;
  const showMobileAndroidStoreAction =
    showGooglePlayBadge &&
    isAndroidBrowser &&
    shouldShowImmersiveShellChrome &&
    activeTab === "listen";

  return (
    <MotionConfig reducedMotion="user">
      <div
        className={`relative ${
          isCanvasVisible ? "h-[100dvh]" : shellLayout === "inline" ? "min-h-0" : "min-h-[100dvh]"
        } ${brand.variant !== "default" ? "bg-school-background" : "bg-[#0f0f14]"} flex flex-col overflow-x-hidden overflow-y-auto`}
      >
        <DecorativeBackground
          isCanvasVisible={isCanvasVisible}
          variant={brand.variant}
          media={brand.backgroundMedia}
          showCinematicOverlay
          showDeskTutor={
            brand.variant === "default" &&
            shouldShowImmersiveShellChrome &&
            activeTab === "listen" &&
            !isHeaderCondensed
          }
          isAuthenticated={isAuthenticated}
        />
        {brand.variant === "ecole" ? (
          <AsciiLogoOverlay
            isVisible={shouldShowImmersiveShellChrome && activeTab === "listen"}
            variant={brand.variant}
            className={`pointer-events-none inset-0 z-[2] hidden overflow-hidden opacity-[0.14] md:block md:opacity-20 ${
              shellLayout === "inline" ? "absolute" : "fixed"
            }`}
          />
        ) : null}
        {/* Wrapper for content to sit above the image */}
        <div className="relative z-20 flex flex-col flex-grow min-h-0">
          {/* Only show header for listen tab and when canvas is not visible */}
          {shouldShowImmersiveShellChrome && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              className="fixed inset-x-0 top-0 z-50 overflow-visible border-b border-transparent bg-transparent"
            >
              <div className="flex h-16 w-full items-center gap-2 px-3 sm:gap-3 sm:px-4 md:h-[72px] md:px-6">
                <div className="flex min-w-0 shrink items-center gap-3 overflow-visible">
                  <ChickyLogo
                    subscribed={subscribed}
                    src={brand.logoSrc}
                    animatedSrc={brand.animatedLogoSrc}
                    alt={brand.logoAlt}
                    condensed={isHeaderCondensed}
                    className={
                      brand.variant === "liceo"
                        ? "h-auto w-[132px] sm:w-[155px] md:w-[170px]"
                        : brand.variant !== "default"
                          ? `${isHeaderCondensed ? "h-10 md:h-12" : "h-12 md:h-14"} w-auto transition-all duration-300`
                          : `${isHeaderCondensed ? "h-10 md:h-11" : subscribed ? "h-12 md:h-14" : "h-14 md:h-16"} w-auto transition-all duration-300`
                    }
                    variant={brand.variant}
                  />
                  <div
                    className={`hidden min-w-0 overflow-hidden transition-all duration-300 lg:block ${
                      isHeaderCondensed
                        ? "max-w-0 border-l-0 pl-0 opacity-0"
                        : "max-w-sm border-l border-white/10 pl-3 opacity-100"
                    }`}
                  >
                    <Tagline
                      text={brand.tagline}
                      subtext={brand.taglineSubtext}
                      linkText={brand.taglineLinkText}
                      linkHref={brand.taglineLinkHref}
                      variant={brand.variant}
                    />
                  </div>
                </div>
                <div className="ml-auto flex min-w-0 items-center justify-end gap-2">
                  {showGooglePlayBadge && (
                    <div className="hidden shrink-0 xl:flex">
                      <GooglePlayBadgeLink placement="homepage_header" />
                    </div>
                  )}
                  <div className="flex min-w-0 items-center justify-end gap-2">
                    {/* Language/Class mismatch warning */}
                    {languageClassWarning && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="hidden max-w-xs rounded-md border border-yellow-500/25 bg-yellow-950/50 px-3 py-2 text-xs text-yellow-100 xl:block"
                        role="status"
                      >
                        {languageClassWarning}
                      </motion.div>
                    )}
                    {showMobileAndroidStoreAction && (
                      <div className="shrink-0 md:hidden">
                        <GooglePlayMobileAction />
                      </div>
                    )}
                    {/* Upgrade button - shown for signed-in non-subscribers on all screens */}
                    <UpgradeButton
                      user={user}
                      subscribed={subscribed}
                      brandVariant={brand.variant}
                    />

                    {/* Streak stays visible on wide screens; the menu owns secondary navigation. */}
                    {user && hasSelectedLanguage && (
                      <motion.div
                        initial={{ opacity: 0, x: 20, scale: 0.9 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 350, damping: 25 }}
                        className="hidden items-center gap-2 xl:flex"
                      >
                        <StreakDisplay
                          streakDisplay={streakDisplay}
                          loading={streakLoading}
                          tooltipAlign="end"
                        />
                      </motion.div>
                    )}

                    {(!isAuthenticated || activeTab === "account") && (
                      <AccountAccess
                        isAuthenticated={isAuthenticated}
                        isLoading={authLoading}
                        isAccountActive={activeTab === "account"}
                        onAccountClick={() =>
                          setActiveTab(activeTab === "account" ? "listen" : "account")
                        }
                        variant={brand.variant}
                      />
                    )}
                    <HamburgerMenu
                      onFeedbackClick={() => setIsFeedbackModalOpen(true)}
                      onHelpClick={handleHelpClick}
                      onShareClick={handleShareClick}
                      onLeaderboardClick={handleLeaderboardClick}
                      onPastConversationsClick={handlePastConversationsClick}
                      onClassroomClick={
                        brand.variant !== "default" || enrolledClasses.length > 0
                          ? handleClassroomClick
                          : undefined
                      }
                      userEmail={user?.email || null}
                      userLevel={userLevel}
                      loading={userLevelLoading}
                      subscriptionManagementUrl={managementUrl}
                      hasSubscriptionAccess={subscribed}
                      brandVariant={brand.variant}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Render active tab content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ type: "spring", stiffness: 400, damping: 35, mass: 0.8 }}
              className="flex-1 flex flex-col min-h-0 w-full"
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>

          {/* Bottom Navigation intentionally removed */}

          {/* Real-time agent component 
        <RealTimeAgent
          currentSegment={currentSegment}
          showTranslation={showTranslation}
          voiceAgentState={agentState}
        /> */}
        </div>

        {/* About modal */}
        <AboutModal isOpen={isAboutModalOpen} onClose={() => setIsAboutModalOpen(false)} />

        {/* How It Works Modal */}
        <HowItWorksModal isOpen={isHowItWorksOpen} onClose={() => setIsHowItWorksOpen(false)} />

        {/* Feedback modal */}
        <FeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={() => setIsFeedbackModalOpen(false)}
          userEmail={user?.email}
        />

        {/* Past Conversations Upgrade Modal */}
        {user?.email && (
          <PastConversationsUpgradeModal
            isOpen={isPastConversationsUpgradeModalOpen}
            onClose={() => setIsPastConversationsUpgradeModalOpen(false)}
            userEmail={user.email}
          />
        )}

        <FreemiumPreSessionOfferModal
          isOpen={showFreemiumPreSessionOffer}
          onClose={dismissFreemiumPreSessionOffer}
          onContinue={continueAfterFreemiumPreSessionOffer}
          userEmail={user?.email}
          usageState={freemiumUsageState}
        />

        {/* Classroom Modal */}
        {user && (
          <ClassroomModal
            isOpen={isClassroomModalOpen}
            onCloseAction={() => setIsClassroomModalOpen(false)}
            enrolledClasses={enrolledClasses}
            selectedClassId={selectedClassId}
            onClassSelectAction={handleClassSelection}
            onJoinClassAction={joinClass}
            isLoading={isLoading}
            error={error}
          />
        )}
      </div>
      {hasSelectedLanguage && isLeaderboardVisible ? children : null}
    </MotionConfig>
  );
};

// // Custom animations
// const styleElement = document.createElement('style');
// styleElement.innerHTML = `
//   @keyframes pulse-slow {
//     0%, 100% {
//       opacity: 0.1;
//       transform: scale(1);
//     }
//     50% {
//       opacity: 0.3;
//       transform: scale(1.05);
//     }
//   }

//   @keyframes pulse-slower {
//     0%, 100% {
//       opacity: 0.15;
//       transform: scale(1);
//     }
//     50% {
//       opacity: 0.35;
//       transform: scale(1.1);
//     }
//   }

//   @keyframes pulse-slowest {
//     0%, 100% {
//       opacity: 0.2;
//       transform: scale(1);
//     }
//     50% {
//       opacity: 0.4;
//       transform: scale(1.15);
//     }
//   }

//   .animate-pulse-slow {
//     animation: pulse-slow 2s ease-in-out infinite;
//   }

//   .animate-pulse-slower {
//     animation: pulse-slower 3s ease-in-out infinite;
//   }

//   .animate-pulse-slowest {
//     animation: pulse-slowest 4s ease-in-out infinite;
//   }
// `;
// document.head.appendChild(styleElement);

export default MainAppView;
